import { useState, type ReactNode } from 'react';

interface ContextualHelpProps {
    /** Texte principal affiché dans la cartouche (visible en permanence). */
    summary: ReactNode;
    /** Contenu détaillé affiché dans le popover au survol/clic du bouton `i`. */
    detailsTitle?: string;
    details?: ReactNode;
    /** Libellé accessible du bouton (par défaut : "Détails"). */
    ariaLabel?: string;
    /** Tonalité visuelle : `info` (bleu, défaut) ou `warning` (orange). */
    variant?: 'info' | 'warning';
}

const VARIANT_STYLES = {
    info: {
        container: 'border-blue-100 bg-blue-50 text-blue-900',
        button: 'border-blue-300 text-blue-600 hover:bg-blue-100',
    },
    warning: {
        container: 'border-amber-200 bg-amber-50 text-amber-900',
        button: 'border-amber-300 text-amber-700 hover:bg-amber-100',
    },
} as const;

/**
 * Cartouche d'aide contextuelle réutilisable.
 * Pattern visuel calqué sur celui introduit par SuiviReglements (#34) :
 * cartouche bleue + bouton `i` + popover au survol/clic.
 */
export function ContextualHelp({
    summary,
    detailsTitle,
    details,
    ariaLabel = 'Détails',
    variant = 'info',
}: ContextualHelpProps) {
    const [showDetails, setShowDetails] = useState(false);
    const hasDetails = Boolean(details);
    const styles = VARIANT_STYLES[variant];

    return (
        <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${styles.container}`}>
            {hasDetails && (
                <div className="relative mt-0.5">
                    <button
                        type="button"
                        className={`flex h-5 w-5 items-center justify-center rounded-full border bg-white text-xs font-medium ${styles.button}`}
                        onMouseEnter={() => setShowDetails(true)}
                        onMouseLeave={() => setShowDetails(false)}
                        onClick={() => setShowDetails(!showDetails)}
                        aria-label={ariaLabel}
                    >
                        i
                    </button>
                    {showDetails && (
                        <div className="absolute z-10 left-6 top-0 w-80 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-lg">
                            {detailsTitle && <p className="mb-1 font-medium">{detailsTitle}</p>}
                            {details}
                        </div>
                    )}
                </div>
            )}
            <div className="leading-relaxed">{summary}</div>
        </div>
    );
}
